import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const RESOLVED_STATUSES = ['Resolved','Resolved - Vendor Case','Resolved - 3 Remainders','Resolved - Known Issues','Resolved - Feature Request','Resolved - Workaround'];
const isResolved = s => RESOLVED_STATUSES.includes(s);

function StatusBadge({ status }) {
  const cls = isResolved(status) ? 'badge-resolved'
    : status==='Open' ? 'badge-open'
    : status==='In Progress' ? 'badge-progress'
    : 'badge-closed';
  return <span className={`badge ${cls}`} style={{fontSize:10}}>{status}</span>;
}
function PriBadge({ priority }) {
  const map = { Low:'badge-low', Medium:'badge-medium', High:'badge-high', Critical:'badge-critical' };
  return <span className={`badge ${map[priority]||'badge-medium'}`}>{priority}</span>;
}

export default function TicketList() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search:'', status:'', priority:'', consultant_id:'' });
  const [consultants, setConsultants] = useState([]);
  const [sortKey, setSortKey] = useState('opened_on');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();
  const { user, isAdmin, canCreate, isSalesMgr } = useAuth();
  const limit = 15;

  const load = async (pg=1, f=filters, sk=sortKey, sd=sortDir) => {
    setLoading(true);
    try {
      const params = { page:pg, limit, ...f, sort_by:sk, sort_dir:sd };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await api.get('/tickets', { params });
      setTickets(data.data); setTotal(data.total); setPage(pg);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get('/lookups/all').then(({ data }) => setConsultants(data.ats_consultants || [])).catch(()=>{});
  }, []);

  const handleFilter = (key, val) => {
    const f = { ...filters, [key]: val };
    setFilters(f); load(1, f);
  };

  const handleSort = (key) => {
    let newDir = 'asc';
    if (sortKey === key) newDir = sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key); setSortDir(newDir);
    load(1, filters, key, newDir);
  };

  // Edit: admin always; user only if ATS consultant AND ticket not resolved
  const canEditTicket = t => {
    if (isAdmin) return true;
    if (user?.role !== 'user') return false;
    if (isResolved(t.status)) return false;
    return t.ats_consultant?.trim().toLowerCase() === user?.full_name?.trim().toLowerCase();
  };

  const totalPages = Math.ceil(total/limit);

  return (
    <Layout title="All Tickets">
      <div className="search-bar">
        <input placeholder="Search ticket ID, description, client..."
          value={filters.search} onChange={e=>handleFilter('search',e.target.value)}/>
        <select value={filters.status} onChange={e=>handleFilter('status',e.target.value)}>
          <option value="">All Statuses</option>
          <option>Open</option>
          <option>In Progress</option>
          <option>Resolved</option>
          <option>Resolved - Vendor Case</option>
          <option>Resolved - 3 Remainders</option>
          <option>Resolved - Known Issues</option>
          <option>Resolved - Feature Request</option>
          <option>Resolved - Workaround</option>
          <option>Closed</option>
        </select>
        <select value={filters.priority} onChange={e=>handleFilter('priority',e.target.value)}>
          <option value="">All Priorities</option>
          {['Low','Medium','High','Critical'].map(p=><option key={p}>{p}</option>)}
        </select>
        {/* ATS Consultant filter — hidden for Sales Manager (already scoped to own tickets) */}
        {!isSalesMgr && (
          <select value={filters.consultant_id} onChange={e=>handleFilter('consultant_id',e.target.value)}>
            <option value="">All ATS Consultants</option>
            {consultants.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <button className="btn btn-secondary"
          onClick={()=>{ const f={search:'',status:'',priority:'',consultant_id:''}; setFilters(f); setSortKey('opened_on'); setSortDir('desc'); load(1,f,'opened_on','desc'); }}>
          <i className="fa-solid fa-xmark"></i> Clear
        </button>
        {canCreate && (
          <button className="btn btn-primary" onClick={()=>navigate('/tickets/new')}>
            <i className="fa-solid fa-plus"></i> New Ticket
          </button>
        )}
      </div>

      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          {loading ? (
            <div style={{padding:40,textAlign:'center',color:'#aaa'}}>
              <i className="fa-solid fa-spinner fa-spin" style={{fontSize:24}}></i>
            </div>
          ) : tickets.length===0 ? (
            <div className="empty-state">
              <i className="fa-solid fa-ticket"></i><div>No tickets found</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  {[
                    { key:'ticket_id',        label:'Ticket ID'      },
                    { key:'client',           label:'Client'         },
                    { key:'issue_short_desc', label:'Description'    },
                    { key:'priority',         label:'Priority'       },
                    { key:'status',           label:'Status'         },
                    { key:'ats_consultant',   label:'ATS Consultant' },
                    { key:'opened_on',        label:'Opened On'      },
                  ].map(col => (
                    <th key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{cursor:'pointer', userSelect:'none', whiteSpace:'nowrap'}}>
                      {col.label}
                      <i className={`fa-solid ${
                        sortKey !== col.key ? 'fa-sort'
                        : sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      }`} style={{marginLeft:6, fontSize:11, color: sortKey===col.key ? '#6382e6' : '#ccc'}}></i>
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t=>(
                  <tr key={t.ticket_id}>
                    <td><span className="ticket-id-link" onClick={()=>navigate(`/tickets/${t.ticket_id}`)}>{t.ticket_id}</span></td>
                    <td>{t.client||'—'}</td>
                    <td style={{maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.issue_short_desc}</td>
                    <td><PriBadge priority={t.priority}/></td>
                    <td><StatusBadge status={t.status}/></td>
                    <td>{t.ats_consultant||'—'}</td>
                    <td style={{fontSize:12,color:'#888'}}>{new Date(t.opened_on).toLocaleDateString()}</td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-sm btn-secondary" onClick={()=>navigate(`/tickets/${t.ticket_id}`)}>
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        {canEditTicket(t) && (
                          <button className="btn btn-sm btn-secondary" onClick={()=>navigate(`/tickets/${t.ticket_id}/edit`)}>
                            <i className="fa-solid fa-pen"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalPages>1 && (
          <div className="pagination" style={{padding:'12px 16px'}}>
            <span>{total} tickets</span>
            <button className="page-btn" disabled={page===1} onClick={()=>load(page-1,filters,sortKey,sortDir)}>‹ Prev</button>
            {Array.from({length:Math.min(totalPages,5)},(_,i)=>i+1).map(p=>(
              <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={()=>load(p,filters,sortKey,sortDir)}>{p}</button>
            ))}
            <button className="page-btn" disabled={page===totalPages} onClick={()=>load(page+1,filters,sortKey,sortDir)}>Next ›</button>
          </div>
        )}
      </div>
    </Layout>
  );
}